import usePersistedFilters from '../../../../hooks/usePersistedFilters'
import useFetchData from '../../../../hooks/UseFetchData'
import Table from '../../../../components/his/invoices/Table'
import PageHeader from '../../../../shared/PageHeader'
import { FaArrowCircleLeft, FaArrowCircleRight, FaFileExcel, FaFilePdf } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { ColumnGroup } from 'primereact/columngroup'
import { Row as ColumnRow } from 'primereact/row'
import { Column } from 'primereact/column'
import XLSX from 'xlsx-js-style'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

import CustomButton from '../../../../shared/CustomButton'
import Filters from '../../../../components/his/doctorSummaryReport/Filters'
import { AmiriRegBase64 } from '../../../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../../../assets/fonts/AmiriB'

const formatNumber = value => Number(value || 0).toLocaleString('en-US')
const formatAmount = value => Number(value || 0).toFixed(2)
const itemTypeValue = (row, itemTypeName, key) => {
  const itemType = (row?.item_types || []).find(item => item.item_type === itemTypeName)
  return itemType?.[key] || 0
}

const buildColumns = itemTypes => t => [
  {
    header: t('Practitioner'),
    bodyCellClass: 'border !border-gray-300',
    body: row => <span className='font-medium'>{row?.doctor || '--'}</span>
  },
  {
    header: t('Department'),
    bodyCellClass: 'border !border-gray-300',
    body: row => row?.department || '--'
  },
  ...itemTypes.flatMap(itemType => [
    {
      header: t('Items'),
      bodyCellClass: 'border !border-gray-300 text-left',
      body: row => formatNumber(itemTypeValue(row, itemType, 'items'))
    },
    {
      header: t('Amount'),
      bodyCellClass: 'border !border-gray-300 text-left',
      body: row => formatAmount(itemTypeValue(row, itemType, 'amount'))
    }
  ])
]

const exportToExcel = (data, t, payloadData) => {
  if (!data?.length) return
  const itemTypes = getItemTypes(data)
  const reportTitle = t('Practitioner Item Summary Report')
  const orgName = payloadData?.business_en || payloadData?.business_ar || 'Organization'
  const location = payloadData?.address || 'Location'
  const periodText = `${t('Daily collection summary for the period from')} ${moment(payloadData?.start_date).format('DD/MM/YYYY')} ${t('and upto')} ${moment(payloadData?.end_date).format('DD/MM/YYYY')}`
  const headerRow1 = [t('Doctor Information'), '', ...itemTypes.flatMap(itemType => [itemType, ''])]
  const headerRow2 = [
    t('Practitioner'),
    t('Department'),
    ...itemTypes.flatMap(() => [t('Items'), t('Amount')])
  ]
  const body = data.map(row => [
    row?.doctor || '',
    row?.department || '',
    ...itemTypes.flatMap(itemType => [
      itemTypeValue(row, itemType, 'items'),
      Number(itemTypeValue(row, itemType, 'amount') || 0)
    ])
  ])
  const worksheet = XLSX.utils.aoa_to_sheet([
    [orgName],
    [location],
    [reportTitle],
    [`${t('Printed On')}: ${moment().format('DD/MM/YYYY HH:mm')}`],
    [periodText],
    [],
    headerRow1,
    headerRow2,
    ...body
  ])

  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headerRow2.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headerRow2.length - 1 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: headerRow2.length - 1 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: headerRow2.length - 1 } },
    { s: { r: 4, c: 0 }, e: { r: 4, c: headerRow2.length - 1 } },
    { s: { r: 6, c: 0 }, e: { r: 6, c: 1 } },
    ...itemTypes.map((_, index) => {
      const startCol = 2 + index * 2
      return { s: { r: 6, c: startCol }, e: { r: 6, c: startCol + 1 } }
    })
  ]
  worksheet['!cols'] = [
    { wch: 24 },
    { wch: 22 },
    ...itemTypes.flatMap(() => [{ wch: 12 }, { wch: 16 }])
  ]

  const range = XLSX.utils.decode_range(worksheet['!ref'])
  const headerStyle = {
    fill: { patternType: 'solid', fgColor: { rgb: '1A365D' } },
    font: { color: { rgb: 'FFFFFF' }, bold: true, sz: 10, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
  }
  const subHeaderStyle = {
    fill: { patternType: 'solid', fgColor: { rgb: 'E5E7EB' } },
    font: { color: { rgb: '111827' }, bold: true, sz: 9, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    border: {
      top: { style: 'thin', color: { rgb: '000000' } },
      bottom: { style: 'thin', color: { rgb: '000000' } },
      left: { style: 'thin', color: { rgb: '000000' } },
      right: { style: 'thin', color: { rgb: '000000' } }
    }
  }
  const titleStyle = {
    font: { bold: true, sz: 13, color: { rgb: '111827' }, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center' }
  }
  const metaStyle = {
    font: { sz: 10, color: { rgb: '374151' }, name: 'Calibri' },
    alignment: { horizontal: 'center', vertical: 'center' }
  }
  const dataStyle = {
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'hair', color: { rgb: 'CCCCCC' } },
      bottom: { style: 'hair', color: { rgb: 'CCCCCC' } },
      left: { style: 'hair', color: { rgb: 'CCCCCC' } },
      right: { style: 'hair', color: { rgb: 'CCCCCC' } }
    }
  }
  for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex++) {
    for (let colIndex = range.s.c; colIndex <= range.e.c; colIndex++) {
      const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })
      if (!worksheet[cellRef]) continue
      if (rowIndex === 0 || rowIndex === 2) {
        worksheet[cellRef].s = titleStyle
      } else if (rowIndex === 1 || rowIndex === 3 || rowIndex === 4) {
        worksheet[cellRef].s = metaStyle
      } else if (rowIndex === 6) {
        worksheet[cellRef].s = headerStyle
      } else if (rowIndex === 7) {
        worksheet[cellRef].s = subHeaderStyle
      } else if (rowIndex > 7) {
        worksheet[cellRef].s = {
          ...dataStyle,
          alignment: { horizontal: colIndex <= 1 ? 'left' : 'center', vertical: 'center' }
        }
      }
    }
  }

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Practitioner Item Summary')
  XLSX.writeFile(workbook, 'practitioner-item-summary-report.xlsx')
}

const exportToPDF = (data, t, payloadData) => {
  if (!data?.length) return
  const itemTypes = getItemTypes(data)
  const doc = new jsPDF('landscape')

  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')

  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 5
  const currentDate = moment().format('DD/MM/YYYY HH:mm')
  const startDate = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const endDate = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const reportTitle = t('Practitioner Item Summary Report')
  const orgName = payloadData?.business_en || payloadData?.business_ar || 'Organization'
  const location = payloadData?.address || 'Location'

  const logoSrc = payloadData?.logo?.startsWith?.('data:')
    ? payloadData.logo
    : payloadData?.logo
      ? `data:image/png;base64,${payloadData.logo}`
      : ''

  doc.setFillColor(26, 54, 93)
  doc.rect(0, 0, pageWidth, 24, 'F')
  if (logoSrc) {
    try {
      doc.addImage(logoSrc, 'PNG', margin, 2, 16, 16)
    } catch (_) {}
  }

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(12)
  doc.setFont('Amiri', 'bold')
  doc.text(orgName, margin + 20, 9)
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  if (location) doc.text(location, margin + 20, 15)
  doc.setFontSize(11)
  doc.setFont('Amiri', 'bold')
  doc.text(reportTitle, pageWidth - margin, 9, { align: 'right' })
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 15, { align: 'right' })

  doc.setTextColor(50, 50, 50)
  doc.setFontSize(7)
  doc.setFont('Amiri', 'normal')
  const periodText = `${t('Daily collection summary for the period from')} ${startDate || '-'} ${t('and upto')} ${endDate || '-'}`
  doc.text(`${t('Period')}: ${startDate} - ${endDate}`, margin, 29)
  doc.text(periodText, pageWidth - margin, 29, { align: 'right' })
  doc.setDrawColor(200, 200, 200)
  doc.setLineWidth(0.3)
  doc.line(margin, 31.5, pageWidth - margin, 31.5)

  const tableHeaders = [
    [
      t('Practitioner'),
      t('Department'),
      ...itemTypes.flatMap(itemType => [itemType, ''])
    ],
    [
      '',
      '',
      ...itemTypes.flatMap(() => [t('Items'), t('Amount')])
    ]
  ]
  const tableData = data.map(row => [
    {
      content: row?.doctor || '',
      styles: { font: 'Amiri' }
    },
    {
      content: row?.department || '',
      styles: { font: 'Amiri' }
    },
    ...itemTypes.flatMap(itemType => [
      {
        content: formatNumber(itemTypeValue(row, itemType, 'items')),
        styles: { font: 'Amiri', halign: 'left' }
      },
      {
        content: formatAmount(itemTypeValue(row, itemType, 'amount')),
        styles: { font: 'Amiri', halign: 'left' }
      }
    ])
  ])

  autoTable(doc, {
    startY: 35,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    styles: { font: 'Amiri', fontSize: 7, cellPadding: 1.5 },
    headStyles: {
      fillColor: [26, 54, 93],
      textColor: 255,
      fontStyle: 'bold',
      font: 'Amiri'
    },
    columnStyles: {
      0: { halign: 'left', cellWidth: 34 },
      1: { halign: 'left', cellWidth: 30 }
    },
    didParseCell: data => {
      if (data.section === 'head') {
        const rowIndex = data.row.index
        const columnIndex = data.column.index

        if (rowIndex === 0 && columnIndex >= 2 && (columnIndex - 2) % 2 === 0) {
          data.cell.colSpan = 2
          data.cell.styles.halign = 'center'
        }
        if (rowIndex === 0 && columnIndex >= 2 && (columnIndex - 2) % 2 === 1) {
          data.cell.text = ['']
        }
        if (rowIndex === 1 && columnIndex < 2) {
          data.cell.text = ['']
        }
        if (rowIndex === 1) {
          data.cell.styles.fillColor = [229, 231, 235]
          data.cell.styles.textColor = [17, 24, 39]
        }
      }
    }
  })
  doc.save('practitioner-item-summary-report.pdf')
}

const getItemTypes = data => {
  const itemTypes = new Set()
  data?.forEach(row => {
    row?.item_types?.forEach(itemType => {
      if (itemType?.item_type) itemTypes.add(itemType.item_type)
    })
  })
  return Array.from(itemTypes).sort()
}

const PractitionerItemSummaryReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const auth = useSelector(state => state.auth)
  const { data: requestData, executeFetch: requestFetch, loading } = useFetchData()
  const { filters: payloadData, setFilters: setPayloadData } = usePersistedFilters(
    'practitioner_item_summary_report',
    {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      practitioner: '',
      department_id: ''
    }
  )

  const reportData = requestData?.data || []
  const itemTypes = getItemTypes(reportData)

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/summary', {
      ...payload,
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      type: 'physician_revenue_productivity'
    })
  }

  const tableColumns = buildColumns(itemTypes)

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow style={{ alignItems: 'end', borderWidth: 1 }}>
        <Column header={t('Doctor Information')} colSpan={2} align='center' />
        {itemTypes.map(itemType => (
          <Column key={itemType} header={itemType} colSpan={2} align='center' />
        ))}
      </ColumnRow>
      <ColumnRow>
        <Column header={t('Practitioner')} />
        <Column header={t('Department')} />
        {itemTypes.flatMap(itemType => [
          <Column key={`${itemType}-items`} header={t('Items')} />,
          <Column key={`${itemType}-amount`} header={t('Amount')} />
        ])}
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div className='main-page-container !overflow-y-auto scrollbar'>
      <div className='flex items-center justify-between'>
        <PageHeader
          title={t('Practitioner Item Summary Report')}
          iconSize={20}
          Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight}
          onIconClick={() => navigate(-1)}
        />
      </div>

      <div>
        <div className='mb-2 flex w-full items-center justify-between gap-2'>
          <Filters
            payloadData={payloadData}
            setPayloadData={setPayloadData}
            requestReport={requestReport}
          />
          <div className='flex gap-2'>
            <CustomButton
              title={t('Export to Excel')}
              buttonType='success'
              icon={FaFileExcel}
              className='h-[35px] w-fit'
              onClick={() => exportToExcel(reportData, t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address
              })}
            />
            <CustomButton
              title={t('Export to PDF')}
              buttonType='danger'
              icon={FaFilePdf}
              className='h-[35px] w-fit'
              onClick={() => exportToPDF(reportData, t, {
                ...payloadData,
                business_en: auth?.buisnessInfo?.name_en,
                business_ar: auth?.buisnessInfo?.name_ar,
                address: auth?.buisnessInfo?.address,
                logo: auth?.buisnessInfo?.logo
              })}
            />
          </div>
        </div>

        <Table
          data={reportData}
          loading={loading}
          columns={tableColumns}
          headerColumnGroup={headerGroup}
          showGridlines
        />
      </div>
    </div>
  )
}

export default PractitionerItemSummaryReport
